import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import ProfileSection from './components/ProfileSection';
import SubscriptionSection from './components/SubscriptionSection';
import NotificationsSection from './components/NotificationsSection';
import IntegrationsSection from './components/IntegrationsSection';
import SecuritySection from './components/SecuritySection';
import TeamSection from './components/TeamSection';
import DangerZoneSection from './components/DangerZoneSection';

const UserProfileSettings = () => {
  const navigate = useNavigate();
  const { userProfile, refreshProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'profile';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [remainingCredits] = useState(100);
  const [monthlyLimit] = useState(500);

  const isTeamTier = userProfile?.subscription_tier === 'team';

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'subscription', label: 'Subscription', icon: 'CreditCard' },
    { id: 'security', label: 'Security', icon: 'Shield' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell', disabled: true, badge: 'Coming soon' },
    { id: 'integrations', label: 'Integrations', icon: 'Plug', disabled: true, badge: 'Coming soon' },
    ...(isTeamTier ? [{ id: 'team', label: 'Team Management', icon: 'UsersRound' }] : []),
    { id: 'danger', label: 'Danger Zone', icon: 'AlertTriangle' }
  ];

  // Re-fetch profile on mount to sync latest data (e.g. after admin changes subscription)
  useEffect(() => {
    refreshProfile();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeTab]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileSection />;
      case 'subscription':
        return <SubscriptionSection />;
      case 'security':
        return <SecuritySection />;
      case 'notifications':
        return <NotificationsSection />;
      case 'integrations':
        return <IntegrationsSection />;
      case 'team':
        return <TeamSection />;
      case 'danger':
        return <DangerZoneSection />;
      default:
        return <ProfileSection />;
    }
  };

  return (
    <>
      <Helmet>
        <title>Account Settings - Roomwork.ai</title>
        <meta name="description" content="Manage your account settings, subscription, security preferences, and integrations for Roomwork.ai platform" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header remainingCredits={remainingCredits} monthlyLimit={monthlyLimit} />

        <main className="pt-20 lg:pt-24 pb-12">
          <div className="container mx-auto px-4">
            <div className="mb-8">
              <button
                onClick={() => navigate('/main-dashboard')}
                className="lg:hidden flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-4"
              >
                <Icon name="ArrowLeft" size={18} />
                <span className="text-sm font-medium">Back to Dashboard</span>
              </button>
              <h1 className="text-3xl font-bold text-white mb-2">Account Settings</h1>
              <p className="text-white/50">Manage your account preferences and settings</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
              <aside className="lg:w-64 flex-shrink-0">
                <div className="lg:hidden mb-4">
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 glassmorphic-card rounded-2xl text-white"
                  >
                    <span className="font-medium">
                      {tabs?.find(tab => tab?.id === activeTab)?.label}
                    </span>
                    <Icon name={isMobileMenuOpen ? 'ChevronUp' : 'ChevronDown'} size={20} />
                  </button>
                </div>

                <nav className={`glassmorphic-card rounded-2xl overflow-hidden ${isMobileMenuOpen ? 'block' : 'hidden lg:block'}`}>
                  {tabs?.map((tab) => {
                    const isActive = activeTab === tab?.id;
                    const isDisabled = tab?.disabled;
                    return (
                      <button
                        key={tab?.id}
                        disabled={isDisabled}
                        onClick={() => {
                          if (isDisabled) return;
                          setActiveTab(tab?.id);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`relative w-full flex items-center gap-3 px-4 py-3.5 text-left transition-all duration-300 ${
                          isDisabled ? 'text-white/25 cursor-not-allowed' :
                          isActive ? 'text-orange-400' : 'text-white/70 hover:text-white'
                        }`}
                        style={isActive && !isDisabled ? { background: 'rgba(254, 131, 56, 0.08)' } : {}}
                      >
                        {isActive && !isDisabled && (
                          <div
                            className="absolute left-0 top-0 bottom-0 w-[3px]"
                            style={{
                              background: 'linear-gradient(to bottom, transparent, rgba(254, 131, 56, 0.8) 30%, rgba(254, 131, 56, 0.8) 70%, transparent)',
                              boxShadow: '0 0 8px rgba(254, 131, 56, 0.4), 0 0 20px rgba(254, 131, 56, 0.15)',
                            }}
                          />
                        )}
                        <Icon name={tab?.icon} size={20} />
                        <span className="font-medium text-sm">{tab?.label}</span>
                        {tab?.badge && (
                          <span className="ml-auto text-[10px] text-white/30 font-medium">{tab.badge}</span>
                        )}
                      </button>
                    );
                  })}
                </nav>

                <div className="hidden lg:block mt-6 glassmorphic-card rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} className="text-orange-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white mb-1">Need Help?</p>
                      <p className="text-xs text-white/40 mb-3">
                        Contact our support team for assistance with your account settings.
                      </p>
                      <a
                        href="mailto:info@roomwork.ai"
                        className="text-xs text-orange-400 hover:text-orange-300 font-medium transition-colors"
                      >
                        info@roomwork.ai
                      </a>
                    </div>
                  </div>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                {renderTabContent()}
              </div>
            </div>
          </div>
        </main>

        <footer className="py-6 mt-12" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-white/30">
                &copy; {new Date()?.getFullYear()} Roomwork.ai. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <a href="#" className="text-sm text-white/30 hover:text-orange-400 transition-colors">Privacy Policy</a>
                <a href="#" className="text-sm text-white/30 hover:text-orange-400 transition-colors">Terms of Service</a>
                <a href="#" className="text-sm text-white/30 hover:text-orange-400 transition-colors">Help Center</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default UserProfileSettings;
